import React, { useEffect, useState } from "react";
import { Modal, Alert, Button, Form, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import { uploadImage, updatePracticeURL } from "../../api";

export function AddPracticeLogo({ practiceId, practiceLogoURL, show, onHide, onLogoUpdate }) {
  const [closeAlert, setCloseAlert] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(practiceLogoURL || "");
  const [submitting, setSubmitting] = useState(false);
  const [hiddenForSuccess, setHiddenForSuccess] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    if (show) {
      setHiddenForSuccess(false);
      setCloseAlert(false);
      setSelectedFile(null);
      setPreviewImage(practiceLogoURL || "");
    }
  }, [show, practiceLogoURL]);

  const handleClose = () => {
    // Nothing chosen yet: an empty close should just close.
    if (!selectedFile) {
      onHide();
      return;
    }
    setCloseAlert(true);
  };

  const handleCancelNo = () => {
    setCloseAlert(false);
  };

  const handleCancelYes = () => {
    setCloseAlert(false);
    setSelectedFile(null);
    setPreviewImage(practiceLogoURL || "");
    onHide();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || submitting) return;

    const formData = new FormData();
    formData.append("image", selectedFile);
    formData.append("practiceId", practiceId);

    setSubmitting(true);
    try {
      const response = await uploadImage(formData);
      await updatePracticeURL(practiceId, response.imageUrl);
      showToast("success", "Logo updated successfully.");
      setHiddenForSuccess(true);
      onLogoUpdate();
      onHide();
    } catch (error) {
      showToast("error", "Image upload failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal show={show && !hiddenForSuccess} onHide={handleClose} size="lg" centered>
        <Modal.Header
          closeButton={!closeAlert}
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            Edit Logo
          </Modal.Title>
        </Modal.Header>

        {closeAlert && (
          <Alert
            variant="info"
            className="d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0"
          >
            <span>Are you sure you want to close? The selected logo will be discarded.</span>
            <div className="ms-auto d-flex gap-2">
              <OverlayTrigger placement="top" overlay={<Tooltip>Discard and close</Tooltip>}>
                <Button variant="outline-primary" onClick={handleCancelYes}>
                  <i className="fa-solid fa-check" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Keep editing</Tooltip>}>
                <Button variant="outline-secondary" onClick={handleCancelNo}>
                  <i className="fa-solid fa-xmark" />
                </Button>
              </OverlayTrigger>
            </div>
          </Alert>
        )}

        <Modal.Body className="text-center">
          <div className="mb-3">
            {previewImage ? (
              <img
                src={previewImage}
                alt="Practice logo preview"
                width={200}
                height={200}
                className="border rounded"
                style={{ objectFit: "cover" }}
              />
            ) : (
              <div
                className="border rounded d-inline-flex align-items-center justify-content-center text-muted"
                style={{ width: 200, height: 200 }}
              >
                <i className="fa-solid fa-image fa-2x" />
              </div>
            )}
          </div>

          <Form.Group controlId="formFile" className="mb-2">
            <Form.Label>Upload New Logo</Form.Label>
            <Form.Control type="file" accept="image/*" onChange={handleFileChange} />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile || submitting}
            style={{ minWidth: 140 }}
          >
            {submitting ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <i className="fa-solid fa-upload me-2" />
                Save Logo
              </>
            )}
          </Button>
          <Button variant="outline-secondary" onClick={handleClose} style={{ minWidth: 140 }}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </>
  );
}
